// Shopify theme sync marker: atlanta-discovery-directory-v1-2026-06-21.
import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { createSoAtlantaClient, normalizeBusinesses, readJsonScript } from '@theme/so-atlanta-api';
import { getFreshLocationIfAlreadyGranted, isGeolocationAvailable, requestFreshLocationFromUser } from '@theme/so-atlanta-location';
import { RECOMMENDATION_TIP, escapeAttr, escapeHtml, renderDisclosure, renderEmpty, renderError, renderTrustBadges } from '@theme/so-atlanta-renderers';
import { venueProfileUrl } from '@theme/so-atlanta-urls';

const LISTING_TABS = [
  { id: 'ALL', label: 'All', statusLabel: 'All listings', singular: 'listing', plural: 'listings' },
  { id: 'PLACE', label: 'Places', statusLabel: 'Places', singular: 'place', plural: 'places' },
  { id: 'SERVICE', label: 'Services', statusLabel: 'Services', singular: 'service', plural: 'services' },
];

const TYPE_LABELS = {
  PLACE: 'Place',
  SERVICE: 'Service',
};

const TRENDING_TITLES = {
  PLACE: 'Trending Places',
  SERVICE: 'Trending Services',
};

const PARTNER_COPY = {
  ALL: {
    prompt: 'Own or manage a business here? Claim it to manage how it shows up on So Atlanta.',
  },
  PLACE: {
    prompt: 'Own a place in this directory? Claim it to manage the profile.',
  },
  SERVICE: {
    prompt: 'Run a local service here? Claim it to manage how it appears.',
  },
};

const PAGE_SIZE_MAX = 100;
const TRENDING_MODULE_LIMIT = 5;
const LISTING_LOAD_ROOT_MARGIN = '0px 0px 640px 0px';

class BusinessDirectoryComponent extends Component {
  settings = {};
  client = null;
  listings = [];
  categories = [];
  searchQuery = '';
  selectedType = 'ALL';
  selectedCategory = '';
  selectedSignal = '';
  selectedSort = 'recommended';
  openNowOnly = false;
  userLocation = null;
  locationState = 'idle';
  locationMessage = '';
  trendingByType = emptyTrendingByType();
  trendingBySlug = new Map();
  requestId = 0;
  nextOffset = 0;
  totalListings = null;
  hasMoreListings = true;
  isLoadingListingsPage = false;
  pageErrorMessage = '';
  loadedListingKeys = new Set();
  listingPageObserver = null;

  #debouncedLoadListings = debounce(() => {
    void this.#loadListings();
  }, 180);

  connectedCallback() {
    super.connectedCallback();
    this.settings = readJsonScript(this, '[data-business-directory-settings]');
    this.client = createSoAtlantaClient(this.settings);
    this.selectedType = this.#validType(this.settings.defaultType || this.settings.defaultGroup || 'ALL');
    this.#bindControls();
    this.#populateCategoryFilters();
    this.#renderTypeTabs();
    this.#renderControlVisibility();
    this.#renderPartnerCta();
    void this.#loadCategories();
    void this.#loadTrendingContext();
    void this.#loadListings();
    void this.#loadGrantedLocation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.requestId += 1;
    this.#teardownListingPageControls();
  }

  #bindControls() {
    const search = this.refs.directorySearchInput;
    if (search instanceof HTMLInputElement) {
      search.addEventListener('input', () => {
        this.searchQuery = search.value.trim();
        this.#debouncedLoadListings();
      });
    }

    const category = this.refs.directoryCategorySelect;
    if (category instanceof HTMLSelectElement) {
      category.addEventListener('change', () => {
        this.selectedCategory = category.value;
        void this.#loadListings();
      });
    }

    const signal = this.refs.directorySignalSelect;
    if (signal instanceof HTMLSelectElement) {
      signal.addEventListener('change', () => {
        this.selectedSignal = signal.value;
        void this.#loadListings();
      });
    }

    const sort = this.refs.directorySortSelect;
    if (sort instanceof HTMLSelectElement) {
      sort.addEventListener('change', () => {
        this.selectedSort = sort.value || 'recommended';
        this.#renderControlVisibility();
        void this.#loadListings();
      });
    }

    const location = this.refs.directoryLocationButton;
    if (location instanceof HTMLButtonElement) {
      location.addEventListener('click', () => this.#requestLocation());
    }

    const openNow = this.refs.directoryOpenNowToggle;
    if (openNow instanceof HTMLInputElement) {
      openNow.addEventListener('change', () => {
        this.openNowOnly = openNow.checked;
        void this.#loadListings();
      });
    }
  }

  async #loadTrendingContext() {
    if (!this.client) return;

    try {
      const [places, services] = await Promise.all([
        this.client.trendingPlaces({ limit: 12 }).catch((error) => {
          console.warn('Discovery directory: trending places unavailable', error);
          return null;
        }),
        this.client.trendingServices({ limit: 12 }).catch((error) => {
          console.warn('Discovery directory: trending services unavailable', error);
          return null;
        }),
      ]);

      this.trendingByType = {
        PLACE: trendingContextFromResponse(places, 'PLACE'),
        SERVICE: trendingContextFromResponse(services, 'SERVICE'),
      };
      this.trendingBySlug = new Map(
        Object.values(this.trendingByType)
          .flatMap((context) => Array.from(context.bySlug.entries()))
      );
      this.#renderTypeTabs();
      this.#renderTrendingRails();
      if (this.listings.length) this.#renderListings();
    } catch (error) {
      console.warn('Discovery directory: trending context unavailable', error);
    }
  }

  async #loadListings() {
    const list = this.refs.directoryList;
    const requestId = ++this.requestId;
    this.#resetListingPagination();

    if (list instanceof HTMLElement) {
      list.setAttribute('aria-busy', 'true');
      list.innerHTML = `<div class="business-directory__loading sa-state sa-state--loading" role="status">${escapeHtml(
        this.settings.loadingText || 'Loading listings...'
      )}</div>`;
    }

    try {
      await this.#loadNextListingsPage({ requestId, initial: true });
      if (requestId !== this.requestId) return;
      this.#renderTypeTabs();
      this.#renderControlVisibility();
      this.#renderPartnerCta();
      this.#renderTrendingRails();
    } catch (error) {
      console.error('Discovery directory: API failed', error);
      if (requestId !== this.requestId) return;
      this.#teardownListingPageControls();
      if (list instanceof HTMLElement) list.innerHTML = renderError(this.settings.errorText);
      this.#setStatus('The directory could not load. Try refreshing the page.');
    } finally {
      if (requestId === this.requestId && list instanceof HTMLElement) list.removeAttribute('aria-busy');
    }
  }

  #resetListingPagination() {
    this.#teardownListingPageControls();
    this.listings = [];
    this.loadedListingKeys = new Set();
    this.nextOffset = 0;
    this.totalListings = null;
    this.hasMoreListings = true;
    this.isLoadingListingsPage = false;
    this.pageErrorMessage = '';
  }

  async #loadNextListingsPage({ requestId = this.requestId, initial = false } = {}) {
    if (!this.client || this.isLoadingListingsPage || (!initial && !this.hasMoreListings)) return;

    this.isLoadingListingsPage = true;
    this.pageErrorMessage = '';
    if (!initial) this.#renderListingPageControls();

    try {
      const listings = await this.#fetchNextListingBatch(requestId);
      if (requestId !== this.requestId) return;

      const sortedListings = this.#sortListings(listings);
      for (const listing of sortedListings) {
        const key = businessKey(listing);
        if (key) this.loadedListingKeys.add(key);
      }

      this.listings = [...this.listings, ...sortedListings];

      if (initial) {
        this.#renderListings();
      } else {
        this.#appendRenderedListings(sortedListings);
      }
    } catch (error) {
      if (requestId !== this.requestId) return;
      if (initial) throw error;
      console.error('Discovery directory: next page failed', error);
      this.pageErrorMessage = 'More listings could not load. Try again.';
      this.#renderListingPageControls();
    } finally {
      if (requestId === this.requestId) {
        this.isLoadingListingsPage = false;
        this.#renderListingPageControls();
      }
    }
  }

  async #fetchNextListingBatch(requestId) {
    const limit = this.#pageSize();
    const listings = [];

    while (requestId === this.requestId && this.hasMoreListings && (listings.length === 0 || this.#shouldFillFilteredBatch(listings.length))) {
      const offset = this.nextOffset;
      const data = await this.client.listings({
        ...this.#listingQueryParams(limit, offset),
      });
      if (requestId !== this.requestId) return [];

      const rawPage = normalizeBusinesses(data?.listings || data?.businesses);
      const pagination = data?.pagination || {};
      const pageLimit = Number(pagination.limit || limit);
      const pageOffset = Number.isFinite(Number(pagination.offset)) ? Number(pagination.offset) : offset;
      const total = Number(pagination.total);
      if (Number.isFinite(total)) this.totalListings = total;

      const filteredPage = this.#postFilterListings(rawPage);
      for (const listing of filteredPage) {
        const key = businessKey(listing);
        if (!key || this.loadedListingKeys.has(key) || listings.some((item) => businessKey(item) === key)) continue;
        listings.push(listing);
      }

      this.nextOffset = pageOffset + pageLimit;
      const totalListings = Number.isFinite(this.totalListings) ? this.totalListings : this.nextOffset + rawPage.length;
      this.hasMoreListings = rawPage.length > 0 && this.nextOffset < totalListings;
      if (!rawPage.length) break;
      if (!this.#usesClientSidePageFilter()) break;
    }

    if (!this.hasMoreListings && this.totalListings == null) {
      this.totalListings = this.listings.length + listings.length;
    }

    return listings;
  }

  #pageSize() {
    return Math.min(Math.max(Number(this.settings.limit || 50), 10), PAGE_SIZE_MAX);
  }

  #usesClientSidePageFilter() {
    return this.selectedType === 'PLACE';
  }

  #shouldFillFilteredBatch(count) {
    return this.#usesClientSidePageFilter() && count < this.#pageSize();
  }

  #listingQueryParams(limit, offset) {
    const params = {
      limit,
      offset,
      sort: this.selectedSort === 'name' || this.selectedSort === 'recent' ? this.selectedSort : 'featured',
    };

    if (this.selectedType !== 'ALL') params.type = this.selectedType;
    if (this.searchQuery) params.search = this.searchQuery;
    if (this.selectedCategory) params.category = this.selectedCategory;
    if (this.selectedSignal === 'approved') params.journeyStatus = 'so_atlanta_approved';
    if (this.selectedSignal === 'confirmed') params.serviceDetails = 'confirmed';
    if (this.openNowOnly && this.selectedType !== 'SERVICE') params.openNow = true;

    return params;
  }

  #postFilterListings(listings) {
    let filtered = listings;
    if (this.selectedType === 'PLACE') {
      filtered = filtered.filter((listing) => this.#isVisitWorthyPlace(listing));
    }
    return filtered;
  }

  #sortListings(listings) {
    const sorted = [...listings];

    if (this.selectedSort === 'name') {
      return sorted.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    }

    if (this.selectedSort === 'recent') {
      return sorted.sort((a, b) => this.#dateValue(b) - this.#dateValue(a) || this.#recommendedTieBreak(a, b));
    }

    if (this.selectedSort === 'nearby' && this.#supportsNearbySort() && this.userLocation) {
      return sorted.sort((a, b) => this.#distanceFor(a) - this.#distanceFor(b) || this.#recommendedTieBreak(a, b));
    }

    return sorted.sort((a, b) => this.#recommendedTieBreak(a, b));
  }

  #renderListings() {
    const list = this.refs.directoryList;
    if (!(list instanceof HTMLElement)) return;

    this.#renderStatus(this.listings.length);

    if (!this.listings.length) {
      this.#teardownListingPageControls();
      list.innerHTML = this.#renderEmptyState();
      this.#bindResetButtons();
      return;
    }

    list.innerHTML = this.listings.map((listing) => this.#renderListingCard(listing)).join('');
    this.#renderListingPageControls();
  }

  #appendRenderedListings(listings) {
    const list = this.refs.directoryList;
    if (!(list instanceof HTMLElement)) return;

    if (!this.listings.length || !list.querySelector('.business-directory__card')) {
      this.#renderListings();
      return;
    }

    this.#teardownListingPageControls();
    if (listings.length) {
      list.insertAdjacentHTML('beforeend', listings.map((listing) => this.#renderListingCard(listing)).join(''));
    }
    this.#renderStatus(this.listings.length);
    this.#renderListingPageControls();
  }

  #renderListingPageControls() {
    const list = this.refs.directoryList;
    if (!(list instanceof HTMLElement)) return;

    this.#teardownListingPageControls();

    if (!this.hasMoreListings && !this.isLoadingListingsPage) return;
    if (!this.listings.length && !this.isLoadingListingsPage) return;

    list.insertAdjacentHTML('beforeend', this.#renderListingPageControlMarkup());
    this.#bindListingPageControls();
    this.#setupListingPageObserver();
  }

  #renderListingPageControlMarkup() {
    return `
      <div class="business-directory__load-sentinel" data-directory-load-sentinel aria-hidden="true"></div>
      <div class="business-directory__load-more" data-directory-load-controls>
        ${
          this.pageErrorMessage
            ? `<p class="business-directory__load-error" role="alert">${escapeHtml(this.pageErrorMessage)}</p>`
            : ''
        }
        <button
          type="button"
          class="button button-secondary business-directory__load-more-btn"
          data-directory-load-more
          aria-busy="${this.isLoadingListingsPage ? 'true' : 'false'}"
          ${this.isLoadingListingsPage ? 'disabled' : ''}
        >
          ${escapeHtml(this.isLoadingListingsPage ? 'Loading more...' : 'Load more')}
        </button>
      </div>
    `;
  }

  #bindListingPageControls() {
    const list = this.refs.directoryList;
    if (!(list instanceof HTMLElement)) return;

    const button = list.querySelector('[data-directory-load-more]');
    if (!(button instanceof HTMLButtonElement)) return;
    button.addEventListener('click', () => {
      void this.#loadNextListingsPage();
    });
  }

  #setupListingPageObserver() {
    const list = this.refs.directoryList;
    if (!(list instanceof HTMLElement) || typeof IntersectionObserver !== 'function') return;

    const sentinel = list.querySelector('[data-directory-load-sentinel]');
    if (!(sentinel instanceof HTMLElement)) return;

    this.listingPageObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        void this.#loadNextListingsPage();
      },
      { rootMargin: LISTING_LOAD_ROOT_MARGIN }
    );
    this.listingPageObserver.observe(sentinel);
  }

  #teardownListingPageControls() {
    if (this.listingPageObserver) {
      this.listingPageObserver.disconnect();
      this.listingPageObserver = null;
    }

    const list = this.refs.directoryList;
    if (!(list instanceof HTMLElement)) return;

    for (const element of list.querySelectorAll('[data-directory-load-sentinel], [data-directory-load-controls]')) {
      element.remove();
    }
  }

  #renderTypeTabs() {
    const wrap = this.refs.directoryGroups;
    if (!(wrap instanceof HTMLElement)) return;

    wrap.innerHTML = LISTING_TABS.map((tab) => {
      const selected = this.selectedType === tab.id;
      return `
        <button
          type="button"
          class="business-directory__type-tab${selected ? ' business-directory__type-tab--active' : ''}"
          role="tab"
          aria-selected="${selected ? 'true' : 'false'}"
          data-directory-type="${escapeAttr(tab.id)}"
        >
          <span>${escapeHtml(tab.label)}</span>
        </button>
      `;
    }).join('');

    wrap.onclick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const tab = target.closest('[data-directory-type]');
      if (!(tab instanceof HTMLButtonElement)) return;
      const nextType = this.#validType(tab.dataset.directoryType || 'ALL');
      if (nextType === this.selectedType) return;
      this.selectedType = nextType;
      this.#coerceControlsForType();
      this.#renderTypeTabs();
      this.#renderControlVisibility();
      this.#renderPartnerCta();
      void this.#loadListings();
    };
  }

  #renderTrendingRails() {
    const wrap = this.refs.directoryTrending;
    if (!(wrap instanceof HTMLElement)) return;

    if (this.searchQuery) {
      wrap.hidden = true;
      wrap.innerHTML = '';
      return;
    }

    const types = this.selectedType === 'ALL' ? ['PLACE', 'SERVICE'] : [this.selectedType];
    const modules = types
      .map((type) => this.#renderTrendingModule(type, this.trendingByType[type]?.items || []))
      .filter(Boolean);

    wrap.hidden = modules.length === 0;
    wrap.innerHTML = modules.join('');
  }

  #renderTrendingModule(type, items) {
    const visibleItems = items.slice(0, TRENDING_MODULE_LIMIT);
    if (!visibleItems.length) return '';

    return `
      <section class="business-directory__trend-module" aria-label="${escapeAttr(TRENDING_TITLES[type] || 'Trending listings')}">
        <div class="business-directory__trend-module-head">
          <h2>
            ${escapeHtml(TRENDING_TITLES[type] || 'Trending')}
            ${renderDisclosure('Recommendation', RECOMMENDATION_TIP, {
              className: 'business-directory__trend-disclosure sa-disclosure--icon-only',
            })}
          </h2>
        </div>
        <div class="business-directory__trend-list">
          ${visibleItems.map((listing) => this.#renderTrendingItem(listing)).join('')}
        </div>
      </section>
    `;
  }

  #renderTrendingItem(listing) {
    const href = venueProfileUrl(listing, this.settings.venueDirectoryPath);
    const rank = this.#trendRankFor(listing);
    const type = listingType(listing);
    return `
      <a class="business-directory__trend-item" href="${escapeAttr(href || '#')}">
        <span class="business-directory__trend-rank">${Number.isFinite(rank) ? rank : ''}</span>
        <span class="business-directory__trend-text">
          <span class="business-directory__trend-name">${escapeHtml(listing.name || 'Untitled listing')}</span>
          <span class="business-directory__trend-meta">${escapeHtml([TYPE_LABELS[type], listing.category].filter(Boolean).join(' / '))}</span>
        </span>
      </a>
    `;
  }

  #renderListingCard(listing) {
    const href = venueProfileUrl(listing, this.settings.venueDirectoryPath);
    const type = listingType(listing);
    const categories = this.#categoryList(listing);
    const category = categories[0] || TYPE_LABELS[type] || 'Listing';
    const description = listing.shortDescription || listing.description || listing.whyGo || listing.locationSummary || '';
    const distance = this.#distanceLabelFor(listing);
    const trendRank = this.#trendRankFor(listing);
    const isFeatured = Boolean(listing.featured);
    const isApproved = listing.soAtlantaApproved === true;
    const isTrending = Number.isFinite(trendRank);
    const cardClasses = [
      'business-directory__card',
      `business-directory__card--${type.toLowerCase()}`,
      isTrending && trendRank === 1 ? 'business-directory__card--top-trending' : '',
      isFeatured ? 'business-directory__card--featured' : '',
      isApproved ? 'business-directory__card--approved' : '',
    ].filter(Boolean).join(' ');
    const badges = [
      this.#openStatusBadge(listing),
      renderTrustBadges(listing.trustBadges, 'business-directory__card-badge'),
      isTrending ? `<span class="business-directory__card-badge business-directory__card-badge--top">#${trendRank} Trending</span>` : '',
      isFeatured ? '<span class="business-directory__card-badge business-directory__card-badge--featured">Featured</span>' : '',
    ].filter(Boolean).join('');
    const meta = this.#listingMeta(listing);
    const detail = this.#listingDetail(listing);

    return `
      <a class="${cardClasses}" role="listitem" href="${escapeAttr(href || '#')}" aria-label="${escapeAttr(
        listing.name || 'Listing'
      )}"${isTrending ? ' data-trending="true"' : ''}${isFeatured ? ' data-featured="true"' : ''}${isApproved ? ' data-so-atlanta-approved="true"' : ''}>
        <span class="business-directory__card-body">
          <span class="business-directory__card-topline">
            <span class="business-directory__card-type">${escapeHtml(TYPE_LABELS[type] || 'Listing')}</span>
            <span class="business-directory__card-kicker">${escapeHtml(category)}</span>
            ${badges}
            ${distance ? `<span class="business-directory__distance">${escapeHtml(distance)}</span>` : ''}
          </span>
          <span class="business-directory__card-title">${escapeHtml(listing.name || 'Untitled listing')}</span>
          ${description ? `<span class="business-directory__card-description">${escapeHtml(description)}</span>` : ''}
          ${meta ? `<span class="business-directory__card-meta">${meta}</span>` : ''}
          ${detail ? `<span class="business-directory__card-detail">${escapeHtml(detail)}</span>` : ''}
          ${
            categories.length > 1
              ? `<span class="business-directory__card-tags">${categories
                  .slice(1, 4)
                  .map((item) => `<span>${escapeHtml(item)}</span>`)
                  .join('')}</span>`
              : ''
          }
          <span class="business-directory__card-cta">${escapeHtml(this.#ctaLabelFor(listing))}</span>
        </span>
      </a>
    `;
  }

  #listingMeta(listing) {
    const type = listingType(listing);

    if (type === 'SERVICE') {
      return escapeHtml(listing.service?.area || listing.serviceArea || 'Atlanta service area');
    }

    const address = listing.address || [listing.city, listing.state].filter(Boolean).join(', ');
    return [listing.neighborhood, address]
      .filter(Boolean)
      .map((item) => `<span>${escapeHtml(item)}</span>`)
      .join('');
  }

  #listingDetail(listing) {
    const type = listingType(listing);

    if (type === 'SERVICE') {
      return listing.service?.contactMethod || listing.service?.bookingUrl || listing.service?.portfolioUrl || listing.tiktokUrl || '';
    }

    return [listing.openStatus !== 'unknown' ? listing.openStatusLabel : '', listing.visitSignal || listing.locationSummary || listing.hoursText || '']
      .filter(Boolean)
      .join(' / ');
  }

  #openStatusBadge(listing) {
    if (listingType(listing) !== 'PLACE' || !listing.openStatus || listing.openStatus === 'unknown') return '';
    const status = listing.openStatus === 'open' ? 'open' : 'closed';
    const label = listing.openStatusLabel || (status === 'open' ? 'Open now' : 'Closed now');
    return `<span class="business-directory__card-badge business-directory__card-badge--${status}">${escapeHtml(label)}</span>`;
  }

  #ctaLabelFor(listing) {
    const type = listingType(listing);
    if (type === 'SERVICE') return 'Book or contact';
    if (type === 'PLACE') return 'View place';
    return 'View listing';
  }

  #renderStatus(count) {
    const status = this.refs.directoryStatus;
    if (!(status instanceof HTMLElement)) return;

    const tab = LISTING_TABS.find((item) => item.id === this.selectedType) || LISTING_TABS[0];
    const resultLabel = count === 1 ? tab.singular : tab.plural;
    const total = Number(this.totalListings);
    const countText = Number.isFinite(total) && total > count ? `${count} of ${total}` : String(count);
    const parts = [`${countText} ${resultLabel}`, tab.statusLabel];

    if (this.selectedCategory) parts.push(this.#selectedCategoryLabel());
    if (this.selectedSignal) parts.push(this.#selectedSignalLabel());
    if (this.searchQuery) parts.push(`Search: "${this.searchQuery}"`);
    if (this.openNowOnly) parts.push('Open now');
    if (this.selectedSort === 'nearby' && this.#supportsNearbySort() && !this.userLocation) {
      parts.push('Use location to sort nearby');
    }
    if (this.#supportsNearbySort() && this.locationMessage) parts.push(this.locationMessage);

    status.textContent = parts.filter(Boolean).join(' / ');
  }

  #renderPartnerCta() {
    const callout = this.refs.directoryPartnerCallout;
    const prompt = this.refs.directoryPartnerPrompt;
    const claimLink = this.refs.directoryPartnerClaimLink;
    if (!(callout instanceof HTMLElement)) return;

    const copy = PARTNER_COPY[this.selectedType] || PARTNER_COPY.ALL;
    if (prompt instanceof HTMLElement) prompt.textContent = copy.prompt;
    if (claimLink instanceof HTMLAnchorElement) {
      claimLink.textContent = 'Claim your business';
      claimLink.href = this.settings.partnerClaimUrl || this.settings.partnerListingUrl || claimLink.href;
    }
  }

  #renderControlVisibility() {
    const supportsNearby = this.#supportsNearbySort();
    const location = this.refs.directoryLocationButton;
    const nearbyOption = this.querySelector('[data-directory-nearby-option]');
    const openNow = this.refs.directoryOpenNowToggle;
    const sort = this.refs.directorySortSelect;

    if (location instanceof HTMLButtonElement) location.hidden = !supportsNearby || this.settings.showLocationButton === false;
    if (openNow instanceof HTMLInputElement) {
      openNow.disabled = this.selectedType === 'SERVICE';
      if (this.selectedType === 'SERVICE' && openNow.checked) {
        openNow.checked = false;
        this.openNowOnly = false;
      }
    }
    if (nearbyOption instanceof HTMLOptionElement) {
      nearbyOption.hidden = !supportsNearby;
      nearbyOption.disabled = !supportsNearby;
    }

    if (sort instanceof HTMLSelectElement && this.selectedSort === 'nearby' && !supportsNearby) {
      this.selectedSort = 'recommended';
      sort.value = 'recommended';
    }
  }

  #populateCategoryFilters() {
    const select = this.refs.directoryCategorySelect;
    if (!(select instanceof HTMLSelectElement)) return;

    select.innerHTML = `
      <option value="">All categories</option>
      ${this.categories
        .map((category) => `<option value="${escapeAttr(category.slug)}">${escapeHtml(category.name)}</option>`)
        .join('')}
    `;
    select.value = this.selectedCategory;
  }

  async #loadCategories() {
    if (!this.client?.categories) return;
    try {
      const data = await this.client.categories();
      const categories = Array.isArray(data?.categories) ? data.categories : [];
      this.categories = categories
        .map((category) => ({
          slug: String(category?.slug || '').trim(),
          name: String(category?.name || '').trim(),
        }))
        .filter((category) => category.slug && category.name);
      this.#populateCategoryFilters();
      this.#renderStatus(this.listings.length);
    } catch (error) {
      console.warn('Discovery directory: categories unavailable', error);
      this.categories = [];
      this.#populateCategoryFilters();
    }
  }

  #setStatus(message) {
    const status = this.refs.directoryStatus;
    if (status instanceof HTMLElement) status.textContent = message;
  }

  #renderEmptyState() {
    return `
      <div class="business-directory__empty">
        ${renderEmpty(this.settings.emptyText || 'No listings match these filters.')}
        <button type="button" class="button button-secondary" data-directory-reset>Reset filters</button>
      </div>
    `;
  }

  #bindResetButtons() {
    const list = this.refs.directoryList;
    if (!(list instanceof HTMLElement)) return;
    const reset = list.querySelector('[data-directory-reset]');
    if (!(reset instanceof HTMLButtonElement)) return;
    reset.addEventListener('click', () => {
      this.searchQuery = '';
      this.selectedType = 'ALL';
      this.selectedCategory = '';
      this.selectedSignal = '';
      this.selectedSort = 'recommended';
      this.openNowOnly = false;
      this.locationMessage = '';

      const search = this.refs.directorySearchInput;
      if (search instanceof HTMLInputElement) search.value = '';
      const category = this.refs.directoryCategorySelect;
      if (category instanceof HTMLSelectElement) category.value = '';
      const signal = this.refs.directorySignalSelect;
      if (signal instanceof HTMLSelectElement) signal.value = '';
      const sort = this.refs.directorySortSelect;
      if (sort instanceof HTMLSelectElement) sort.value = 'recommended';
      const openNow = this.refs.directoryOpenNowToggle;
      if (openNow instanceof HTMLInputElement) openNow.checked = false;

      this.#renderTypeTabs();
      this.#renderControlVisibility();
      this.#renderPartnerCta();
      void this.#loadListings();
    });
  }

  async #loadGrantedLocation() {
    const location = await getFreshLocationIfAlreadyGranted({ timeout: 6000 });
    if (!location) return;

    this.userLocation = location;
    this.locationState = 'ready';
    this.locationMessage = 'Nearby distances on';
    const button = this.refs.directoryLocationButton;
    if (button instanceof HTMLButtonElement) button.textContent = 'Update location';
    if (this.#supportsNearbySort()) {
      this.#renderControlVisibility();
      void this.#loadListings();
    }
  }

  async #requestLocation() {
    if (!this.#supportsNearbySort()) return;
    const button = this.refs.directoryLocationButton;
    if (!isGeolocationAvailable()) {
      this.locationState = 'unavailable';
      this.locationMessage = 'Location is unavailable in this browser';
      this.#renderStatus(this.listings.length);
      return;
    }

    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.textContent = 'Locating...';
    }
    this.locationState = 'loading';
    this.locationMessage = 'Finding nearby places';
    this.#renderStatus(this.listings.length);

    try {
      this.userLocation = await requestFreshLocationFromUser({ timeout: 10000 });
      this.locationState = 'ready';
      this.locationMessage = 'Nearby distances on';
      this.selectedSort = 'nearby';
      const sort = this.refs.directorySortSelect;
      if (sort instanceof HTMLSelectElement) sort.value = 'nearby';
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
        button.textContent = 'Update location';
      }
      void this.#loadListings();
    } catch (_) {
      this.locationState = 'denied';
      this.locationMessage = 'Location permission was not granted';
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
        button.textContent = 'Use my location';
      }
      this.#renderStatus(this.listings.length);
    }
  }

  #coerceControlsForType() {
    if (!this.#supportsNearbySort() && this.selectedSort === 'nearby') {
      this.selectedSort = 'recommended';
      const sort = this.refs.directorySortSelect;
      if (sort instanceof HTMLSelectElement) sort.value = 'recommended';
    }
    if (this.selectedType === 'SERVICE' && this.openNowOnly) {
      this.openNowOnly = false;
      const openNow = this.refs.directoryOpenNowToggle;
      if (openNow instanceof HTMLInputElement) openNow.checked = false;
    }
  }

  #supportsNearbySort() {
    return this.selectedType === 'ALL' || this.selectedType === 'PLACE';
  }

  #isVisitWorthyPlace(listing) {
    if (listingType(listing) !== 'PLACE') return false;
    if (listing.visitIntent === 'ERRAND') return false;
    if (listing.dropMeOffEligible === true) return true;
    return listing.showOnMap !== false && listing.latitude != null && listing.longitude != null;
  }

  #categoryList(listing) {
    const categories = Array.isArray(listing.categories) ? listing.categories : [];
    return [...new Set([...categories, listing.category].filter(Boolean).map((category) => String(category).trim()))];
  }

  #dateValue(listing) {
    const raw = listing.lastUpdated || listing.lastContributionAt || listing.updatedAt;
    const value = raw ? Date.parse(raw) : 0;
    return Number.isFinite(value) ? value : 0;
  }

  #recommendedTieBreak(a, b) {
    const aTrend = this.#trendRankFor(a);
    const bTrend = this.#trendRankFor(b);
    if (Number.isFinite(aTrend) !== Number.isFinite(bTrend)) return Number.isFinite(aTrend) ? -1 : 1;
    if (Number.isFinite(aTrend) && Number.isFinite(bTrend) && aTrend !== bTrend) return aTrend - bTrend;
    if (Boolean(a.soAtlantaApproved) !== Boolean(b.soAtlantaApproved)) return a.soAtlantaApproved ? -1 : 1;
    if (Boolean(a.featured) !== Boolean(b.featured)) return a.featured ? -1 : 1;
    const rankDiff = Number(b.sortOrder || 0) - Number(a.sortOrder || 0);
    if (rankDiff) return rankDiff;
    return String(a.name || '').localeCompare(String(b.name || ''));
  }

  #trendRankFor(listing) {
    const trend = this.trendingBySlug.get(businessSlug(listing));
    return trend?.rank || Number.POSITIVE_INFINITY;
  }

  #selectedCategoryLabel() {
    return this.categories.find((item) => item.slug === this.selectedCategory)?.name || this.selectedCategory;
  }

  #selectedSignalLabel() {
    const labels = {
      approved: 'So Atlanta Approved',
      confirmed: 'Confirmed Services',
    };
    return labels[this.selectedSignal] || this.selectedSignal;
  }

  #distanceFor(listing) {
    if (!this.userLocation) return Number.POSITIVE_INFINITY;
    if (!this.#canShowDistanceFor(listing)) return Number.POSITIVE_INFINITY;
    const latitude = Number(listing.latitude);
    const longitude = Number(listing.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return Number.POSITIVE_INFINITY;
    return distanceMiles(this.userLocation.latitude, this.userLocation.longitude, latitude, longitude);
  }

  #distanceLabelFor(listing) {
    if (!this.#supportsNearbySort() || !this.userLocation || !this.#canShowDistanceFor(listing)) return '';
    return this.#formatDistance(this.#distanceFor(listing));
  }

  #canShowDistanceFor(listing) {
    if (listing.latitude == null || listing.longitude == null) return false;
    if (listingType(listing) !== 'SERVICE') return true;
    return hasStreetLocation(listing);
  }

  #formatDistance(miles) {
    if (!Number.isFinite(miles)) return '';
    if (miles < 0.1) return 'Less than 0.1 mi';
    if (miles < 10) return `${miles.toFixed(1)} mi`;
    return `${Math.round(miles)} mi`;
  }

  #validType(id) {
    const normalized = String(id || 'ALL').toUpperCase();
    if (normalized === 'ALL' || normalized === 'PLACE' || normalized === 'SERVICE') {
      return normalized;
    }
    return 'ALL';
  }
}

function emptyTrendingByType() {
  return {
    PLACE: { items: [], bySlug: new Map() },
    SERVICE: { items: [], bySlug: new Map() },
  };
}

function trendingContextFromResponse(data, expectedType) {
  const source = Array.isArray(data?.items) ? data.items : Array.isArray(data?.videos) ? data.videos : data?.listings || [];
  const items = [];
  const bySlug = new Map();

  source.forEach((item, index) => {
    const listing = normalizeBusinesses([item?.listing || item?.business || item])[0];
    if (!listing || listingType(listing) !== expectedType) return;
    const rank = Number.isFinite(Number(item?.rank)) ? Number(item.rank) : index + 1;
    items.push({ ...listing, trendingRank: rank });
    const slug = businessSlug(listing);
    if (slug) bySlug.set(slug, { rank });
  });

  return { items, bySlug };
}

function listingType(listing) {
  return String(listing?.listingType || listing?.type || 'PLACE').toUpperCase();
}

function hasStreetLocation(listing) {
  const service = listing?.service && typeof listing.service === 'object' ? listing.service : {};
  const noAddressReason = String(service.noAddressReason || listing?.noAddressReason || listing?.no_address_reason || '').trim();
  if (noAddressReason) return false;

  const address = String(listing?.address || '').trim();
  if (!looksLikeStreetAddress(address)) return false;

  const serviceArea = String(service.area || listing?.serviceArea || listing?.service_area || '').trim();
  return !serviceArea || serviceArea.toLowerCase() !== address.toLowerCase();
}

function looksLikeStreetAddress(address) {
  return /\d/.test(String(address || '')) && !/^(online|service[-\s]?based|virtual)\b/i.test(String(address || '').trim());
}

function distanceMiles(lat1, lon1, lat2, lon2) {
  const radiusMiles = 3958.8;
  const toRad = (value) => (value * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return radiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function businessSlug(business) {
  return String(business?.slug || business?.handle || business?.shopifyMetaobjectHandle || '').trim();
}

function businessKey(business) {
  return businessSlug(business) || String(business?.name || '').trim();
}

if (!customElements.get('business-directory-component')) {
  customElements.define('business-directory-component', BusinessDirectoryComponent);
}
